import { keepPreviousData, staleTimes, useQuery } from '@platform/api-client';
import { Combobox } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { nameIn, type Named } from '../../catalog/categories.ts';
import { useProduct } from '../../catalog/useCatalogQueries.ts';
import { TYPING_DELAY_MS, useDebouncedValue } from '../../useDebouncedValue.ts';
import { queryKeys, usePageContext } from '../../usePageContext.ts';

const RESULT_PAGE_SIZE = 20;
const ANY_STATUS = 'all';

export type ProductFilterProps = {
  api: TypedClient;
  productId: string | null;
  onProductChange: (productId: string | null) => void;
};

type ProductChoice = { readonly value: string; readonly label: string };

type NamedProduct = Named & { readonly id: string; readonly reference: string | null };

const useProductSearch = (api: TypedClient, typed: string) => {
  const { tenantId } = usePageContext();
  const search = useDebouncedValue(typed.trim(), TYPING_DELAY_MS);
  return useQuery({
    queryKey: queryKeys.products(tenantId, { view: 'dashboardFilter', search }),
    queryFn: () =>
      api.get('/admin/products', {
        query: { status: ANY_STATUS, page_size: RESULT_PAGE_SIZE, ...(search === '' ? {} : { search }) },
      }),
    staleTime: staleTimes.catalog,
    placeholderData: keepPreviousData,
  });
};

export const ProductFilter = ({ api, productId, onProductChange }: ProductFilterProps) => {
  const { t, locale } = usePageContext();
  const [typed, setTyped] = useState('');
  const found = useProductSearch(api, typed);
  const chosen = useProduct(api, productId).data;
  const choiceOf = (product: NamedProduct): ProductChoice => ({
    value: product.id,
    label: nameIn(product, locale, product.reference ?? product.id),
  });
  const results: readonly ProductChoice[] = (found.data?.items ?? []).map(choiceOf);
  const kept =
    chosen === undefined || results.some((choice) => choice.value === chosen.id) ? [] : [choiceOf(chosen)];
  return (
    <Combobox
      label={t('dashboard.filters.product')}
      value={productId ?? ''}
      options={[{ value: '', label: t('dashboard.filters.allProducts') }, ...kept, ...results]}
      searchPlaceholder={t('dashboard.filters.findProduct')}
      noMatchLabel={t('dashboard.filters.noProductMatch')}
      onQueryChange={setTyped}
      onChange={(value) => {
        onProductChange(value === '' ? null : value);
      }}
    />
  );
};
